import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import Card from './Card';
import LoadingSpinner from './LoadingSpinner';
import { fetchCardsByUserIDPaginated } from '../api/cards';


const PAGE_SIZE = 50;

const PositionPickerModal = ({ position, usedPlayerIDs, hasCurrent, onSelect, onRemove, onClose, extraFilters }) => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const offsetRef = useRef(0);
    const observerRef = useRef(null);
    const sentinelRef = useRef(null);

    const filterYear = extraFilters?.year || '';
    const filterTeams = extraFilters?.team || [];
    const filterRating = extraFilters?.rating || '';
    const filterTeamKey = Array.isArray(filterTeams) ? filterTeams.join(',') : String(filterTeams);

    const fetchCards = useCallback(async (offset = 0) => {
        const params = {
            sort: 'rating',
            dir: 'DESC',
            limit: PAGE_SIZE,
            offset,
        };
        if (position === 'DH' || position === 'Bench') {
            params.type = 'Batter';
        } else if (['LF', 'CF', 'RF'].includes(position)) {
            params.position = 'OF';
        } else {
            params.position = position;
        }
        if (filterYear) params.year = filterYear;
        if (filterTeams.length > 0) params.teams = filterTeams.join(',');
        if (filterRating) params.maxRating = filterRating;
        const data = await fetchCardsByUserIDPaginated(params);
        return data;
    }, [position, filterYear, filterTeamKey, filterRating]);

    // Initial load
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const data = await fetchCards(0);
                if (cancelled) return;
                setCards(data.cards || []);
                setHasMore(data.hasMore ?? false);
                offsetRef.current = (data.cards || []).length;
            } catch {
                // ignore
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [fetchCards]);

    // Infinite scroll
    useEffect(() => {
        if (loading || !hasMore) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && hasMore && !loadingMore) {
                    setLoadingMore(true);
                    fetchCards(offsetRef.current).then(data => {
                        setCards(prev => [...prev, ...(data.cards || [])]);
                        setHasMore(data.hasMore ?? false);
                        offsetRef.current += (data.cards || []).length;
                        setLoadingMore(false);
                    }).catch(() => setLoadingMore(false));
                }
            },
            { threshold: 0.1 }
        );

        observerRef.current = observer;
        if (sentinelRef.current) observer.observe(sentinelRef.current);

        return () => observer.disconnect();
    }, [loading, hasMore, loadingMore, fetchCards]);

    return (
        <Modal onClose={onClose} title={`Select ${position}`} className="position-picker-modal">
            <div className="position-picker">
                <h3 className="position-picker__title">Select {position}</h3>
                {loading ? (
                    <LoadingSpinner />
                ) : cards.length === 0 ? (
                    <div className="position-picker__empty">No cards available for {position}</div>
                ) : (
                    <>
                        {hasCurrent && (
                            <div className="position-picker__remove-bar">
                                <button className="btn-danger" onClick={onRemove}>Remove Current Card</button>
                            </div>
                        )}
                        <div className="position-picker__grid">
                            {cards.map(ci => {
                                const card = { ...ci.Card, CardInventory: ci };
                                if (!ci.Card) return null;
                                const isUsed = usedPlayerIDs.has(card.playerID);
                                return (
                                    <div
                                        key={ci.inventoryID}
                                        className={`position-picker__card ${isUsed ? 'position-picker__card--used' : ''}`}
                                        onClick={() => !isUsed && onSelect(ci)}
                                    >
                                        <Card
                                            card={card}
                                            variation={ci.CardVariation || null}
                                            showLabel
                                            hoverFlip
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        {hasMore && (
                            <div ref={sentinelRef} className="position-picker__sentinel">
                                {loadingMore && <LoadingSpinner />}
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

PositionPickerModal.propTypes = {
    position: PropTypes.string.isRequired,
    usedPlayerIDs: PropTypes.instanceOf(Set).isRequired,
    hasCurrent: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onRemove: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    extraFilters: PropTypes.object,
};

export default PositionPickerModal;
